import { Chart, Plugin } from 'chart.js'

const DURATION_MS = 1400

interface RevealPluginOptions {
  epoch?: number
}

/**
 * Left-to-right reveal animation.
 *
 * The clip is applied in beforeDatasetsDraw/afterDatasetsDraw so that it
 * wraps only the dataset rendering. Scales (axis labels, grid lines) are
 * drawn before beforeDatasetsDraw and are therefore never clipped.
 */
export function makeRevealPlugin(id: string): Plugin & { order: number } {
  let startTime: number | null = null
  let lastEpoch: number | undefined

  return {
    id: `reveal-${id}`,
    order: -100,
    beforeDatasetsDraw(chart: Chart) {
      if (!chart.ctx) return

      const options = (chart.options.plugins as Record<string, RevealPluginOptions>)?.[
        `reveal-${id}`
      ]
      const epoch = options?.epoch

      if (epoch !== lastEpoch) {
        startTime = null
        lastEpoch = epoch
      }

      const { ctx, chartArea } = chart
      const now = performance.now()

      startTime ??= now

      const elapsed = now - startTime
      const progress = Math.min(elapsed / DURATION_MS, 1)
      const plotW = chartArea.width * progress

      ctx.save()
      ctx.beginPath()
      ctx.rect(chartArea.left, chartArea.top, plotW, chartArea.height)
      ctx.clip()

      if (progress < 1) {
        requestAnimationFrame(() => {
          if (chart.ctx) chart.draw()
        })
      }
    },
    afterDatasetsDraw(chart: Chart) {
      if (chart.ctx) chart.ctx.restore()
    }
  }
}
